import { handleAsyncStatus } from 'affiny-core/modules/asynchronousState/saga';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { selectAccountId } from 'affiny-core/redux/selectors/accountSelectors';
import {
  selectCurrentPage,
  selectLastMessageAttributesByThreadId,
  selectMessageIdsByThreadId,
  selectThreadIdByOtherAccountId,
  selectTotalItem,
} from 'affiny-core/redux/selectors/threadsSelectors';
import isEmpty from 'lodash/isEmpty';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getThreads } from '../api';
import { NormalizedMessages } from '../api/repositories/messages/messages.normalizer';
import { setThreadAsRead } from '../api/repositories/thread/thread.sagas';
import { Action, ActionsOfType } from '../helpers';
import { checkHasCompletedProfile } from '../helpers/checkSaga';
import { getPaginatedData } from '../helpers/pagination';
import { messagesActions } from '../messages/actions';
import { getMessagesFrom } from '../messages/saga';
import {
  EThreadActionType,
  EThreadsActionType,
  threadActions,
  threadsActions,
  TThreadAction,
  TThreadsAction,
} from './actions';

export function* getThreadsSaga(
  action: ActionsOfType<TThreadsAction, EThreadsActionType.GET>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  try {
    yield call(getPaginatedData, {
      pageSelector: selectCurrentPage,
      totalSelector: selectTotalItem,
      reset: action.payload.reset,
      apiCall: getThreads,
      resetAction: threadsActions.resetThreads,
      successAction: threadsActions.getThreadsSuccess,
    });

    if (action.payload.resolve) action.payload.resolve();
  } catch {
    if (action.payload.reject) action.payload.reject();
  }
}

export function* paginationResetSaga<
  OptionalParam,
  Fn extends (arg: OptionalParam) => any
>({
  apiCall,
  apiCallOptionalParameter,
  resetAction,
  resetActionParameters = [],
  successAction,
  successActionParameters = [],
  reset,
}: {
  apiCall: Fn;
  apiCallOptionalParameter: OptionalParam;
  resetAction: (...parameter: any[]) => Action<string>;
  resetActionParameters?: any[];
  successAction: (response: any, ...parameter: any[]) => Action<string>;
  successActionParameters?: any[];
  reset?: boolean;
}) {
  // eslint-disable-next-line no-useless-catch
  try {
    if (!(yield* checkHasCompletedProfile())) return;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore How to type apiCallOptionalParameter properly ?
    const response = yield call<Fn>(apiCall, apiCallOptionalParameter);

    if (reset) yield put(resetAction(...resetActionParameters));

    yield put(successAction(response, ...successActionParameters));

    return response;
  } catch (e) {
    throw e;
  }
}

export function* initThreadSaga(
  action: ActionsOfType<TThreadAction, EThreadActionType.GET>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  const { otherAccountId } = action.payload;

  const threadId = yield select(selectThreadIdByOtherAccountId, otherAccountId);

  if (!threadId) return;

  yield put(messagesActions.postDryRun({ threadId: threadId }));

  const response: NormalizedMessages = yield call(paginationResetSaga, {
    apiCall: getMessagesFrom,
    apiCallOptionalParameter: {
      reset: true,
      threadId: threadId,
    },
    resetAction: threadActions.resetThread,
    resetActionParameters: [threadId],
    successAction: threadActions.getThreadSuccess,
    successActionParameters: [threadId],
    reset: true,
  });

  if (isEmpty(response.entities.messages)) return;

  const lastMessageAttribute = yield select(
    selectLastMessageAttributesByThreadId,
    threadId
  );

  if (!lastMessageAttribute.readByMe) {
    yield put(threadActions.setThreadAsRead(otherAccountId));
  }
}

function* setMessagesAsRead(accountId: string) {
  if (!(yield* checkHasCompletedProfile())) return;

  const threadId = yield select(selectThreadIdByOtherAccountId, accountId);

  yield call(setThreadAsRead, threadId);
  const ids: string[] = yield select(selectMessageIdsByThreadId, threadId);

  yield put(messagesActions.setMessagesAsRead(ids));
}

export function* setThreadAsReadSaga(
  action: ActionsOfType<TThreadAction, EThreadActionType.SET_AS_READ>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  const { accountId } = action.payload;

  yield call(setMessagesAsRead, accountId);
}

export function* deleteUnsentMessageSaga(
  action: ActionsOfType<TThreadAction, EThreadActionType.DELETE_UNSENT_MESSAGE>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  const { unsentId, accountId } = action.payload;
  const myAccountId = yield select(selectAccountId);

  yield put(
    messagesActions.deleteMessage(unsentId, `${myAccountId}_${accountId}`)
  );
}

export function* threadsRootSaga() {
  yield all([
    takeLatest(
      EThreadActionType.GET,
      handleAsyncStatus(initThreadSaga, EAsyncEventTypes.INIT_THREAD)
    ),
    takeLatest(
      EThreadsActionType.GET,
      handleAsyncStatus(getThreadsSaga, EAsyncEventTypes.THREADS_LIST)
    ),
    takeLatest(
      EThreadActionType.SET_AS_READ,
      handleAsyncStatus(
        setThreadAsReadSaga,
        EAsyncEventTypes.SET_THREAD_AS_READ
      )
    ),
    takeLatest(
      EThreadActionType.DELETE_UNSENT_MESSAGE,
      deleteUnsentMessageSaga
    ),
  ]);
}
